import React, { useState, useEffect } from 'react';
import { personFileService } from '../services/api';
import {
  X, Mail, Monitor, FileText, ListChecks, ShieldCheck, Loader2, Eye,
  CheckCircle2, XCircle, AlertTriangle, Lock, Upload, PenLine, Building2, ExternalLink,
} from 'lucide-react';

const RESP_STYLE = {
  applicant_upload: { verb: 'uploads', Icon: Upload, cls: 'bg-blue-50 text-blue-700' },
  applicant_form: { verb: 'completes', Icon: FileText, cls: 'bg-indigo-50 text-indigo-700' },
  applicant_sign: { verb: 'signs', Icon: PenLine, cls: 'bg-purple-50 text-purple-700' },
  internal: { verb: null, label: 'BlueLock internal', Icon: Building2, cls: 'bg-gray-100 text-gray-600' },
};

const respLabel = (r, first) => (r.verb ? `${first} ${r.verb}` : r.label);

const STATUS_CLS = {
  REQUIRED: 'bg-red-50 text-red-700',
  CONDITIONAL: 'bg-amber-50 text-amber-700',
  VERIFICATION_REQUIRED: 'bg-amber-50 text-amber-700',
  RECEIVED: 'bg-green-50 text-green-700',
};

const TABS = [
  { key: 'email', label: 'Outgoing Email', Icon: Mail },
  { key: 'experience', label: 'Applicant Experience', Icon: Monitor },
  { key: 'items', label: 'Documents & Forms', Icon: FileText },
  { key: 'readiness', label: 'Readiness', Icon: ListChecks },
];

const PacketPreviewModal = ({ personId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('email');
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    (async () => {
      try { setData(await personFileService.sendSimulation(personId)); }
      finally { setLoading(false); }
    })();
  }, [personId]);

  const first = ((data?.applicant?.name || '').trim().split(/\s+/)[0]) || 'The applicant';

  const openDoc = async (docKey, fmt) => {
    try {
      const res = await personFileService.downloadLegalDocument(docKey, fmt);
      if (res?.download_url) window.open(res.download_url, '_blank', 'noopener');
    } catch (e) { /* preview is read-only; ignore download errors */ }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="packet-preview-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-blue-900" /> Preview Packet</h2>
            <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5"><Lock className="w-3 h-3" /> FOUNDER PREVIEW — read-only. No email sent, no token consumed, no applicant state changed.</p>
          </div>
          <button onClick={onClose} data-testid="packet-preview-close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : !data ? (
          <div className="p-8 text-red-600">Failed to load preview.</div>
        ) : (
          <>
            {/* Readiness banner */}
            <div className={`px-6 py-2 text-sm font-medium flex items-center gap-2 ${data.readiness.ready_to_send ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="packet-readiness-banner">
              {data.readiness.ready_to_send ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              READY TO SEND: {data.readiness.ready_to_send ? 'YES' : 'NO'}
              <span className="text-xs font-normal ml-2">· Send mode: document-request · e-signature {data.esignature_enabled ? 'ON' : 'OFF'}</span>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-4 pt-3 border-b border-gray-200">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`packet-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg ${tab === t.key ? 'bg-blue-50 text-blue-900 font-medium border-b-2 border-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}>
                  <t.Icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              {tab === 'email' && <EmailTab email={data.email_preview} />}
              {tab === 'experience' && <ExperienceTab landing={data.applicant_landing} />}
              {tab === 'items' && <ItemsTab applicant={data.applicant_items} internal={data.internal_items} openItem={openItem} setOpenItem={setOpenItem} first={first} onOpenDoc={openDoc} />}
              {tab === 'readiness' && <ReadinessTab readiness={data.readiness} deferred={data.deferred_signatures} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EmailTab = ({ email }) => (
  <div data-testid="packet-email-tab">
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 text-sm space-y-1 border-b border-gray-200">
        <div><span className="text-gray-400 w-16 inline-block">From:</span> {email.from}</div>
        <div><span className="text-gray-400 w-16 inline-block">To:</span> {email.to}</div>
        <div><span className="text-gray-400 w-16 inline-block">Subject:</span> <span className="font-medium">{email.subject}</span></div>
      </div>
      <div className="p-4 text-sm text-gray-800" data-testid="packet-email-body" dangerouslySetInnerHTML={{ __html: email.body_html }} />
    </div>
    <p className="text-xs text-gray-400 mt-2">CTA link (masked for preview): {email.cta_url_display}</p>
  </div>
);

const ExperienceTab = ({ landing }) => (
  <div data-testid="packet-experience-tab">
    <div className="border border-gray-200 rounded-lg p-5">
      <div className="text-center border-b border-gray-100 pb-3 mb-4">
        <div className="text-lg font-bold text-blue-900">{landing.brand}</div>
        <div className="text-sm text-gray-600 mt-1">{landing.welcome}</div>
      </div>
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">What you&apos;ll be asked to complete</p>
      <div className="space-y-2">
        {landing.items.map((it, i) => {
          const r = RESP_STYLE[it.responsibility] || RESP_STYLE.internal;
          return (
            <div key={i} className="flex items-start gap-3 border border-gray-100 rounded-lg p-3">
              <r.Icon className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800">{it.label}
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${STATUS_CLS[it.status] || 'bg-gray-50 text-gray-500'}`}>{it.status}</span>
                </div>
                {it.instructions && <div className="text-xs text-gray-500 mt-0.5">{it.instructions}</div>}
                {it.accepted_types && <div className="text-xs text-gray-400 mt-0.5">Accepted: {it.accepted_types}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-sm text-gray-600 mt-4">{landing.submission_instructions}</p>
    </div>
    <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> {landing.note}</p>
  </div>
);

const ItemsTab = ({ applicant, internal, openItem, setOpenItem, first, onOpenDoc }) => (
  <div data-testid="packet-items-tab" className="space-y-6">
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{first} will receive / complete</h3>
      <div className="space-y-2">
        {applicant.map((it) => <ItemRow key={it.key} it={it} first={first} open={openItem === it.key} onToggle={() => setOpenItem(openItem === it.key ? null : it.key)} onOpenDoc={onOpenDoc} />)}
      </div>
    </div>
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><Building2 className="w-4 h-4 text-gray-500" /> BlueLock internal / verify ({first} is NOT asked for these)</h3>
      <div className="space-y-2">
        {internal.map((it) => <ItemRow key={it.key} it={it} first={first} open={openItem === it.key} onToggle={() => setOpenItem(openItem === it.key ? null : it.key)} onOpenDoc={onOpenDoc} />)}
      </div>
    </div>
  </div>
);

const previewCta = (p) => {
  if (p.type === 'document') return 'View Document';
  if (p.type === 'form') return 'View Form';
  if (p.type === 'upload_request') return 'Preview Request';
  if (p.type === 'internal') return 'Internal Details';
  if (p.type === 'policy_acknowledgment') return 'View Policies';
  return 'Preview Unavailable';
};

const ItemRow = ({ it, open, onToggle, first, onOpenDoc }) => {
  const r = RESP_STYLE[it.responsibility] || RESP_STYLE.internal;
  const p = it.preview || {};
  const badge = it.verified ? { cls: 'bg-green-50 text-green-700', text: 'VERIFIED' }
    : it.received ? { cls: 'bg-blue-50 text-blue-700', text: 'RECEIVED' }
    : { cls: STATUS_CLS[it.status] || 'bg-gray-50 text-gray-500', text: it.status };
  return (
    <div className="border border-gray-200 rounded-lg" data-testid={`packet-item-${it.key}`}>
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="min-w-0 flex items-center gap-2">
          <r.Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-800 truncate">{it.label}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${badge.cls}`} data-testid={`packet-item-badge-${it.key}`}>{badge.text}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${r.cls}`}>{respLabel(r, first)}</span>
          {it.signature_required && <span className="text-purple-500 flex-shrink-0" title="Signature required"><PenLine className="w-3.5 h-3.5" /></span>}
        </div>
        <button onClick={onToggle} data-testid={`packet-item-preview-${it.key}`}
          className={`text-xs font-medium px-2.5 py-1 rounded flex-shrink-0 flex items-center gap-1 ${p.type === 'unavailable' ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}`}>
          <Eye className="w-3.5 h-3.5" /> {previewCta(p)}
        </button>
      </div>
      {open && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm" data-testid={`packet-item-content-${it.key}`}>
          {it.conditional_trigger && <p className="text-xs text-amber-700 mb-2"><AlertTriangle className="w-3 h-3 inline mr-1" />Conditional: {it.conditional_trigger}</p>}
          {p.type === 'document' && (
            <div className="space-y-2" data-testid={`packet-doc-professional-${it.key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{p.title || it.label}</span>
                {p.version && <span className="text-xs text-gray-500">Version {p.version}</span>}
                {p.approval_status === 'founder_approved' && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700">Founder Approved</span>
                )}
                <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{it.status}</span>
                {p.signature_required && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-700">Signature Required</span>
                )}
              </div>
              {p.professional_note && <p className="text-xs text-gray-600">{p.professional_note}</p>}
              {p.signature_note && <p className="text-xs text-purple-700 flex items-center gap-1"><Lock className="w-3 h-3" />{p.signature_note}</p>}
              {p.artifact?.pdf_available && (
                <button onClick={() => onOpenDoc(p.artifact.doc_key, 'pdf')} data-testid={`open-approved-pdf-${it.key}`}
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs font-medium">
                  <ExternalLink className="w-3 h-3" /> Open approved PDF (the professional document the recipient receives)
                </button>
              )}
              {p.artifact?.docx_available && (
                <button onClick={() => onOpenDoc(p.artifact.doc_key, 'docx')} data-testid={`open-approved-docx-${it.key}`}
                  className="inline-flex items-center gap-1 text-gray-500 hover:underline text-xs ml-3">
                  <FileText className="w-3 h-3" /> DOCX
                </button>
              )}
            </div>
          )}
          {p.type === 'form' && (
            <div>
              <div className="font-medium text-gray-800">{p.form_name}</div>
              {p.instructions && <p className="text-gray-600 mt-1">{p.instructions}</p>}
              {p.fields && <ul className="list-disc ml-5 mt-2 text-gray-700 text-xs space-y-0.5">{p.fields.map((f, i) => <li key={i}>{f}</li>)}</ul>}
              {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs mt-2"><ExternalLink className="w-3 h-3" /> Open official form</a>}
            </div>
          )}
          {p.type === 'upload_request' && (
            <div className="space-y-1 text-gray-700">
              <p><span className="text-gray-400">{first} will be told:</span> {p.instructions}</p>
              <p className="text-xs text-gray-400">Accepted: {p.accepted_types}</p>
              <p className="text-xs text-gray-500">{p.after_receipt}</p>
            </div>
          )}
          {p.type === 'internal' && (
            <div className="text-gray-700"><p>{p.detail}</p><p className="text-xs text-gray-500 mt-1">{p.note}</p></div>
          )}
          {p.type === 'policy_acknowledgment' && (
            <div className="space-y-2">
              {p.signature_note && <p className="text-xs text-purple-700 flex items-center gap-1"><Lock className="w-3 h-3" />{p.signature_note}</p>}
              <p className="text-gray-700 text-xs">{p.acknowledgment_statement}</p>
              <ul className="space-y-1">
                {(p.policies || []).map((pol, i) => (
                  <li key={i} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{pol.title}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {pol.version && <span className="text-[11px] text-gray-500">Version {String(pol.version).replace(/^V/i, '')}</span>}
                        {pol.approval_status === 'founder_approved' && <span className="px-1 py-0.5 rounded text-[10px] bg-green-50 text-green-700">Founder Approved</span>}
                        {pol.acknowledgment_required && <span className="px-1 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700">Acknowledgment Required</span>}
                      </div>
                    </div>
                    {pol.doc_key && pol.pdf_available ? (
                      <button onClick={() => onOpenDoc(pol.doc_key, 'pdf')} data-testid={`ack-open-pdf-${pol.code}`}
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs flex-shrink-0 ml-2"><ExternalLink className="w-3 h-3" /> Open approved PDF</button>
                    ) : pol.view_link ? (
                      <a href={pol.view_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs flex-shrink-0 ml-2"><ExternalLink className="w-3 h-3" /> Open</a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.type === 'unavailable' && (
            <p className="text-amber-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> PREVIEW UNAVAILABLE — {p.reason}</p>
          )}
        </div>
      )}
    </div>
  );
};

const ReadinessTab = ({ readiness, deferred }) => (
  <div data-testid="packet-readiness-tab" className="space-y-2">
    {deferred.length > 0 && (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800 mb-3">
        <PenLine className="w-4 h-4 inline mr-1" /> Signature DEFERRED (e-signature disabled): {deferred.join(', ')}
      </div>
    )}
    {readiness.checks.map((c, i) => (
      <div key={i} className="flex items-start gap-2 text-sm border border-gray-100 rounded-lg px-3 py-2" data-testid={`readiness-check-${i}`}>
        {c.status === 'pass' ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
          : c.status === 'warn' ? <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
          : <XCircle className="w-4 h-4 text-red-600 mt-0.5" />}
        <div>
          <div className="font-medium text-gray-800">{c.check}</div>
          <div className="text-xs text-gray-500">{c.detail}</div>
        </div>
      </div>
    ))}
  </div>
);

export default PacketPreviewModal;
